import { useCallback, useEffect, useMemo, useState } from "react";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useSnackbar } from "notistack";
import {
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  NotificationsOffOutlined,
  NotificationsOutlined,
  MoreVert,
  OpenInNew,
} from "@mui/icons-material";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import ReminderModal from "../modals/ReminderModal.jsx";
import { getDatum, getDatumZeit } from "@accredo/fe-shared/services/formatService.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { applyFilterWithAccessorKeys } from "./utils/tableUtils.js";
import { useReminderActions } from "../../hooks/useReminderActions.jsx";
import { colorsFoundation } from "@accredo/fe-shared/theme/index.js";

export default function RemindersTable() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { toggleReminderDone } = useReminderActions();

  const tableKey = "reminders";
  const storageKey = "v2_table_reminders";
  const defaultRowsPerPage = getUserRowsPerPage(user, 15);

  const [columnOrder, setColumnOrder] = useState([]);
  const [activeFilterTab, setActiveFilterTab] = useState("all");
  const [isReminderDialogOpen, setIsReminderDialogOpen] = useState(false);
  const [reminderToEdit, setReminderToEdit] = useState(null);
  const [togglingReminderIds, setTogglingReminderIds] = useState(new Set());
  const [rowMenuState, setRowMenuState] = useState({
    anchorEl: null,
    menuRowId: null,
  });

  const filter = useMemo(() => {
    if (activeFilterTab === "all") {
      return null;
    }
    return { due_period: activeFilterTab };
  }, [activeFilterTab]);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Meine Erinnerungen",
        route: "/data/kommunikation-reminders",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "faellig_am", order: "desc" }],
          include: ["kommunikation.schaden"],
          ...(filter ? { filters: filter } : {}),
        },
      },
    ],
    [defaultRowsPerPage, storageKey, filter]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    updateTableState(tableKey, { shouldFetch: true });
  }, [tableKey]);

  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
  }, [activeFilterTab, tableKey]);

  const handleToggleDone = useCallback(
    async (e, row) => {
      e.stopPropagation();
      const newValue = !row.ist_erledigt;

      setTogglingReminderIds((prev) => {
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });

      const result = await toggleReminderDone(row, newValue);

      if (result) {
        enqueueSnackbar(
          newValue
            ? "Erinnerung als erledigt markiert"
            : "Erinnerung als offen markiert",
          { variant: "success" }
        );
        await fetchData(tableKey);
      }

      setTogglingReminderIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    },
    [enqueueSnackbar, fetchData, toggleReminderDone]
  );

  const handleOpenReminderDialog = useCallback((e, row) => {
    e?.stopPropagation();
    setReminderToEdit(row);
    setIsReminderDialogOpen(true);
  }, []);

  const handleCloseReminderDialog = () => {
    setIsReminderDialogOpen(false);
    setReminderToEdit(null);
  };

  const handleMenuOpen = (e, id) => {
    e.stopPropagation();
    setRowMenuState({ anchorEl: e.currentTarget, menuRowId: id });
  };

  const handleMenuClose = (e) => {
    e?.stopPropagation();
    setRowMenuState({ anchorEl: null, menuRowId: null });
  };

  const getOpenReminderIconColor = (faellig_am) => {
    if (!faellig_am) {
      return "text.secondary";
    }

    const due = new Date(faellig_am);
    due.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (due < today) {
      return colorsFoundation.status.negative[600];
    }
    if (due.getTime() === today.getTime()) {
      return colorsFoundation.status.critical[600];
    }
    return "text.secondary";
  };

  const getKommunikationPath = (kommunikation) => {
    if (!kommunikation?.schaden_id || !kommunikation?.id) {
      return null;
    }
    return `/schaeden/${kommunikation.schaden_id}?dialog=kommunikation&dialog_id=${kommunikation.id}`;
  };

  const handleOpenKommunikation = (e, kommunikation) => {
    e.stopPropagation();
    const path = getKommunikationPath(kommunikation);
    if (!path) {
      return;
    }
    window.open(path, "_blank");
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "ist_erledigt",
        header: "Erledigt",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnHiding: false,
        muiTableBodyCellProps: () => {
          return { align: "center" };
        },
        Cell: ({ row }) => {
          const isLoading = togglingReminderIds.has(row.original.id);
          return (
            <Tooltip
              title={
                row.original.ist_erledigt
                  ? "Als offen markieren"
                  : "Als erledigt markieren"
              }
            >
              <IconButton
                size="small"
                onClick={(e) => handleToggleDone(e, row.original)}
                disabled={isLoading}
                sx={{ p: 0.5 }}
              >
                {isLoading ? (
                  <CircularProgress size="1.25rem" color="primary" />
                ) : row.original.ist_erledigt ? (
                  <NotificationsOffOutlined
                    sx={{
                      fontSize: "1.5rem",
                      color: colorsFoundation.status.positive[600],
                    }}
                  />
                ) : (
                  <NotificationsOutlined
                    sx={{
                      fontSize: "1.5rem",
                      color: getOpenReminderIconColor(row.original.faellig_am),
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "betreff",
        header: "Betreff",
        size: 400,
        enableSorting: false,
        accessorFn: (row) => row.kommunikation?.betreff ?? "",
        Cell: ({ row }) => {
          const path = getKommunikationPath(row.original.kommunikation);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  "&:hover": { textDecoration: "underline" },
                  cursor: "pointer",
                }}
                onClick={(e) =>
                  handleOpenKommunikation(e, row.original.kommunikation)
                }
              >
                {row.original.kommunikation?.betreff}
              </Box>
              {path && (
                <Tooltip title="In neuem Tab öffnen">
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    onClick={(e) =>
                      handleOpenKommunikation(e, row.original.kommunikation)
                    }
                  >
                    <OpenInNew sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "schaden_nummer_accredo",
        header: "Schadennummer",
        size: 150,
        enableSorting: false,
        accessorFn: (row) =>
          row.kommunikation?.schaden?.schaden_nummer_accredo ?? "",
        Cell: ({ row }) => (
          <Box>
            {row.original.kommunikation?.schaden?.schaden_nummer_accredo}
          </Box>
        ),
      },
      {
        accessorKey: "faellig_am",
        header: "Fällig am",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box
            sx={{
              "&:hover": { textDecoration: "underline" },
              cursor: "pointer",
            }}
            onClick={(e) => handleOpenReminderDialog(e, row.original)}
          >
            {getDatum(row.original.faellig_am)}
          </Box>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Erstellt am",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }) => <Box>{getDatumZeit(row.original.created_at)}</Box>,
      },
      {
        id: "actions",
        header: "",
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnHiding: false,
        Cell: ({ row }) => (
          <>
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, row.original.id)}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={rowMenuState.anchorEl}
              open={
                Boolean(rowMenuState.anchorEl) &&
                rowMenuState.menuRowId === row.original.id
              }
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={(e) => {
                  handleMenuClose(e);
                  handleOpenReminderDialog(e, row.original);
                }}
              >
                Erinnerung bearbeiten
              </MenuItem>
            </Menu>
          </>
        ),
      },
    ],
    [handleOpenReminderDialog, rowMenuState, togglingReminderIds]
  );

  useEffect(() => {
    setColumnOrder(columns.map((col) => col.id || col.accessorKey));
  }, [columns]);

  const filterTabs = useMemo(
    () => [
      {
        key: "all",
        label: "Alle",
        onClick: setActiveFilterTab,
      },
      {
        key: "today",
        label: "Heute",
        onClick: setActiveFilterTab,
      },
      {
        key: "tomorrow",
        label: "Morgen",
        onClick: setActiveFilterTab,
      },
      {
        key: "this_week",
        label: "Diese Woche",
        onClick: setActiveFilterTab,
      },
      {
        key: "overdue",
        label: "Überfällig",
        onClick: setActiveFilterTab,
      },
    ],
    []
  );

  return (
    <>
      <DataTable
        variant="enclosed"
        columns={columns}
        data={rows}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        onChangePage={(pagination) => {
          const next =
            typeof pagination === "function"
              ? pagination({
                  pageIndex: tableState.pageIndex,
                  pageSize: tableState.pageSize,
                })
              : pagination;
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
        onChangeFilters={(filters) =>
          updateTableState(tableKey, {
            filters: applyFilterWithAccessorKeys(filters, columns),
          })
        }
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        storageKey={storageKey}
        enableHeader
        enableSorting
        manualFiltering={true}
        enableGlobalFilter={true}
        enableColumnFilters={true}
        enableColumnHiding={true}
        topToolbarIcons={true}
        columnOrder={columnOrder}
        filterTabs={filterTabs}
        activeFilterTab={activeFilterTab}
      />
      <ReminderModal
        open={isReminderDialogOpen}
        onClose={handleCloseReminderDialog}
        onSubmit={() => {
          fetchData(tableKey);
        }}
        kommunikation_id={reminderToEdit?.kommunikation?.id}
        schaden_id={reminderToEdit?.kommunikation?.schaden_id}
        initialEditMode={true}
      />
    </>
  );
}
